/**
 * Upload usage report — prototype usageUpload/upDraw (docs/prototype.html 3282-3324): one Excel or CSV for a
 * whole product; each row names the client and the server matches it to ours.
 *
 * ADR §10.1, four steps, all through the contract: POST /usage-reports/sign → PUT the bytes to storage →
 * POST /usage-reports/{id}/parse (the worker reads it) → poll GET /usage-reports/{id} until the rows are staged.
 * The SPA never reads the file or matches clients itself: the table shows the staged rows as the server read
 * them. Save sends one decision per row (POST /usage-reports/{id}/apply); Cancel discards a staged upload.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { UPLOAD_EXTENSIONS, UPLOAD_MAX_BYTES, normaliseHeader, type Product, type UploadDetail, type UploadRow } from '@amplispot/contracts';
import { clientColumn } from '@amplispot/domain';
import { currentPeriod, discardUpload, nf, parseAndWait, periodLabel, reportPeriods, sendFile, templateHref, useApplyUpload, useProducts } from '../../lib/usage';
import { ErrorState, Field, Loading, Modal, errorText, useToast } from '../../ui/kit';

type Phase = { k: 'idle' } | { k: 'sending' } | { k: 'reading' } | { k: 'staged'; d: UploadDetail } | { k: 'failed'; msg: string };

/** The report's client column as written in the file: the server's own ranked picker (domain clientColumn), so
 * "Customer ID, Customer Name" shows the name the server matched on, not the code. */
function clientInReport(r: UploadRow): string {
  const ks = Object.keys(r.raw);
  const k = ks[clientColumn(ks.map((h) => normaliseHeader(h)))];
  return (k ? r.raw[k]?.trim() : '') || '(blank)';
}

const extrasOf = (p: Product | undefined) => (p?.fields ?? []).filter((f) => !f.is_main);

export function UploadModal({ onClose, productKey }: { onClose: () => void; productKey?: string }) {
  const pr = useProducts();
  const toast = useToast();
  const apply = useApplyUpload();
  const periods = useMemo(() => reportPeriods(), []);
  const now = currentPeriod();

  const [key, setKey] = useState<string | null>(productKey ?? null);
  const [period, setPeriod] = useState(periods[0]!);
  const [phase, setPhase] = useState<Phase>({ k: 'idle' });
  const [pick, setPick] = useState<Record<number, string>>({});
  const [fileKey, setFileKey] = useState(0);
  const staged = useRef<string | null>(null);
  const abort = useRef<AbortController | null>(null);

  const products = useMemo(() => [...(pr.data ?? [])].sort((a, b) => a.sort_order - b.sort_order), [pr.data]);
  const product = products.find((p) => p.key === key) ?? products[0];

  // A staged upload nobody saved is discarded when the pop-up goes (Cancel, ✕, Esc, or leaving the page).
  useEffect(
    () => () => {
      abort.current?.abort();
      if (staged.current) void discardUpload(staged.current).catch(() => undefined);
    },
    [],
  );

  /** Product or period changed after a file was read: that upload belongs to the old choice, so drop it. */
  const dropStaged = () => {
    abort.current?.abort();
    if (staged.current) void discardUpload(staged.current).catch(() => undefined);
    staged.current = null;
    setPhase({ k: 'idle' });
    setPick({});
  };
  /** …and clear the file input, so the next file is read against the new product and period. */
  const reset = () => {
    dropStaged();
    setFileKey((n) => n + 1);
  };

  const choose = async (f: File | undefined) => {
    if (!f || !product) return;
    dropStaged();
    const name = f.name.toLowerCase();
    if (!UPLOAD_EXTENSIONS.some((e) => name.endsWith(e))) return setPhase({ k: 'failed', msg: 'Could not read that file. Save it as .xlsx or .csv.' });
    if (f.size > UPLOAD_MAX_BYTES) return setPhase({ k: 'failed', msg: 'The file is over 10 MB. Split it or remove extra sheets.' });
    const ac = new AbortController();
    abort.current = ac;
    try {
      setPhase({ k: 'sending' });
      const { upload_id } = await sendFile(f, product.key, period);
      staged.current = upload_id;
      setPhase({ k: 'reading' });
      const d = await parseAndWait(upload_id, ac.signal);
      if (ac.signal.aborted) return;
      if (d.upload.status === 'failed') {
        staged.current = null;
        return setPhase({ k: 'failed', msg: d.upload.parse_error ?? 'Could not read that file. Save it as .xlsx or .csv.' });
      }
      if (!d.rows.length) return setPhase({ k: 'failed', msg: 'No rows found in that file.' });
      setPhase({ k: 'staged', d });
    } catch (e) {
      if (ac.signal.aborted) return;
      setPhase({ k: 'failed', msg: errorText(e) });
    }
  };

  const rows = phase.k === 'staged' ? phase.d.rows : [];
  const ready = (r: UploadRow) => !r.unmatched_reason && r.match_kind !== 'none' && (r.match_kind !== 'ambiguous' || !!pick[r.row_number]);
  const ok = rows.filter(ready);

  const save = () => {
    if (phase.k !== 'staged' || !product) return;
    const id = phase.d.upload.id;
    apply.mutate(
      {
        id,
        body: {
          decisions: rows.map((r) =>
            ready(r)
              ? { row_number: r.row_number, decision: 'apply' as const, ...(r.match_kind === 'ambiguous' ? { client_id: pick[r.row_number]! } : {}) }
              : { row_number: r.row_number, decision: 'skip' as const },
          ),
        },
      },
      {
        onSuccess: (res) => {
          staged.current = null;
          const n = res.applied;
          toast(`${product.name} report saved for ${n} client${n === 1 ? '' : 's'}${res.skipped ? `, ${res.skipped} skipped` : ''}. Health updated.`);
          onClose();
        },
      },
    );
  };

  const busy = phase.k === 'sending' || phase.k === 'reading';
  const extras = extrasOf(product);
  const cols = product ? [product.main_metric, ...extras.map((e) => e.label)] : [];

  return (
    <Modal
      wide
      title="Upload usage report"
      sub="Upload the product’s usage report as it comes, one Excel or CSV for the whole product. Each row names the client, and we match it to our clients."
      onClose={onClose}
      footer={
        <>
          <span className="sub">{rows.length ? `${ok.length} of ${rows.length} clients ready` : ''}</span>
          <div className="grow" />
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button className="btn pri" onClick={save} disabled={!ok.length || apply.isPending}>
            {apply.isPending ? 'Saving…' : `Save report${ok.length ? ` for ${ok.length} clients` : ''}`}
          </button>
        </>
      }
    >
      {pr.isLoading ? (
        <Loading label="Loading products…" />
      ) : pr.error ? (
        <ErrorState error={pr.error} onRetry={() => void pr.refetch()} />
      ) : !product ? (
        <div className="note warn">No products are set up yet. Add one in Settings → Products and usage first.</div>
      ) : (
        <>
          <div className="cols c2">
            <Field label="Product">
              <select
                className="in"
                value={product.key}
                disabled={busy}
                onChange={(e) => {
                  setKey(e.target.value);
                  reset();
                }}
              >
                {products.map((p) => (
                  <option key={p.key} value={p.key}>
                    {p.name}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Report period">
              <select
                className="in"
                value={period}
                disabled={busy}
                onChange={(e) => {
                  setPeriod(e.target.value);
                  reset();
                }}
              >
                {periods.map((p) => (
                  <option key={p} value={p}>
                    {periodLabel(p, now)}
                  </option>
                ))}
              </select>
            </Field>
          </div>
          <div className="note" style={{ marginBottom: 12 }}>
            The first column is the <b>client name</b>. Then one column per number, named as in the report: <b>{cols.join(', ')}</b>. Other columns are ignored.{' '}
            <a href={templateHref(product.key)} download style={{ color: 'var(--brand)', fontWeight: 600 }}>
              Download a template
            </a>
          </div>
          <Field label="Excel or CSV file" error={phase.k === 'failed' ? phase.msg : undefined}>
            <input key={fileKey} type="file" className="in" accept={UPLOAD_EXTENSIONS.join(',')} disabled={busy} onChange={(e) => void choose(e.target.files?.[0])} />
          </Field>
          {busy ? <Loading label={phase.k === 'sending' ? 'Sending the file…' : 'Reading the report…'} /> : null}
          {apply.error ? <div className="note bad">{errorText(apply.error)}</div> : null}
          {rows.length ? (
            <div className="panel" style={{ margin: '8px 0 12px', maxHeight: 300, overflow: 'auto' }}>
              <table>
                <thead>
                  <tr>
                    <th>Client in the report</th>
                    <th>Matched to</th>
                    <th>{product.main_metric}</th>
                    <th>Other numbers</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => {
                    const other = extras.filter((e) => r.extras[e.field_key] != null).map((e) => `${e.label} ${nf(r.extras[e.field_key])}`);
                    return (
                      <tr key={r.row_number}>
                        <td>{clientInReport(r)}</td>
                        <td>
                          {r.match_kind === 'ambiguous' && !r.unmatched_reason ? (
                            <select
                              className="in"
                              style={{ width: 'auto' }}
                              aria-label={`Which client is ${clientInReport(r)}?`}
                              value={pick[r.row_number] ?? ''}
                              onChange={(e) => setPick((m) => ({ ...m, [r.row_number]: e.target.value }))}
                            >
                              <option value="">Pick the client…</option>
                              {r.candidates.map((c) => (
                                <option key={c.id} value={c.id}>
                                  {c.name}
                                </option>
                              ))}
                            </select>
                          ) : r.matched_client ? (
                            <b>{r.matched_client.name}</b>
                          ) : (
                            '-'
                          )}
                        </td>
                        <td className="num">{r.main_value != null ? nf(r.main_value) : '-'}</td>
                        <td className="sub">{other.join(', ') || '-'}</td>
                        <td>
                          {r.unmatched_reason ? (
                            // A reason is a sentence ("X has not bought Y"): it wraps in its cell rather than being cut off.
                            <span className="chip warm" style={{ whiteSpace: 'normal', maxWidth: 240, lineHeight: 1.35, borderRadius: 10 }}>
                              {r.unmatched_reason}
                            </span>
                          ) : r.match_kind === 'none' ? (
                            <span className="chip warm">Client not found</span>
                          ) : ready(r) ? (
                            <span className="chip hot">Ready</span>
                          ) : (
                            <span className="chip warm">Pick the client</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : null}
        </>
      )}
    </Modal>
  );
}
